import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Loader2, CheckCircle2, User, Mail, Calendar, Clock, Phone, FileText, Briefcase, Target, Shield, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Helper to detect if message is an appointment confirmation
const isAppointmentConfirmation = (content: string): boolean => {
  return content.includes('Just to confirm') || 
         (content.includes('Name:') && content.includes('Email:') && content.includes('Service Type:'));
};

// Helper to parse appointment details from confirmation message
const parseAppointmentDetails = (content: string) => {
  const lines = content.split('\n').filter(line => line.trim());
  const details: Array<{ label: string; value: string; icon: typeof User }> = [];
  
  let intro = '';
  let question = '';
  
  // Check if it's the inline format (everything in one paragraph)
  const hasInlineFormat = content.includes('you\'re looking to book') || 
                          content.includes('you are looking to book') ||
                          content.includes('to make sure I have everything just right');
  
  if (hasInlineFormat) {
    // Extract intro (first line before the confirmation sentence)
    intro = lines[0] || '';
    
    // Extract the confirmation line
    const confirmLine = lines.find(line => 
      line.includes('looking to book') || line.includes('to make sure')
    ) || '';
    
    // Extract question (last line with "Does all that look correct")
    question = lines.find(line => line.includes('Does all that look correct')) || '';
    
    // Parse inline bold values
    const boldMatches = confirmLine.match(/\*\*([^*]+)\*\*/g);
    
    if (boldMatches && boldMatches.length >= 4) {
      // First bold value is usually the service type
      const serviceType = boldMatches[0].replace(/\*\*/g, '');
      if (serviceType && !serviceType.includes('@')) {
        details.push({ label: 'Service Type', value: serviceType, icon: Briefcase });
      }
      
      // Look for name (usually after "appointment for")
      const nameMatch = confirmLine.match(/appointment for \*\*([^*]+)\*\*/);
      if (nameMatch) {
        details.push({ label: 'Name', value: nameMatch[1], icon: User });
      }
      
      // Look for email (contains @)
      const emailMatch = boldMatches.find(m => m.includes('@'));
      if (emailMatch) {
        details.push({ label: 'Email', value: emailMatch.replace(/\*\*/g, ''), icon: Mail });
      }
      
      // Look for date/time (usually between "on" and comma, or after email)
      const dateMatch = confirmLine.match(/on \*\*([^*]+)\*\*/);
      if (dateMatch) {
        details.push({ label: 'Date & Time', value: dateMatch[1], icon: Calendar });
      }
      
      // Look for phone (usually last, contains numbers and hyphens)
      const phoneMatch = boldMatches.find(m => /\d{3}-\d{3}-\d{4}/.test(m));
      if (phoneMatch) {
        details.push({ label: 'Phone Number', value: phoneMatch.replace(/\*\*/g, ''), icon: Phone });
      }
    }
  } else {
    // Legacy format: separate lines with **Label:** value
    lines.forEach((line, index) => {
      if (index === 0 && !line.includes('**')) {
        intro = line;
        return;
      }
      
      if (line.includes('Does all that look correct')) {
        question = line;
        return;
      }
      
      const match = line.match(/\*+([^:*]+):\*+\s*(.+)/);
      if (match) {
        const label = match[1].trim();
        const value = match[2].trim();
        let icon = User;
        
        if (label === 'Email') icon = Mail;
        else if (label === 'Preferred Date') icon = Calendar;
        else if (label === 'Preferred Time') icon = Clock;
        else if (label === 'Phone Number') icon = Phone;
        else if (label === 'Service Type') icon = Briefcase;
        else if (label === 'Name') icon = User;
        else if (label.includes('Date')) icon = Calendar;
        
        details.push({ label, value, icon });
      }
    });
  }
  
  return { intro, details, question };
};

// Helper to detect if message is a service selection
const isServiceSelection = (content: string): boolean => {
  return (content.includes('what kind of service') || 
          content.includes('What kind of service')) &&
         (content.includes('Individual Tax Return') || 
          content.includes('Business Tax Return'));
};

// Helper to parse service selection details
const parseServiceSelection = (content: string) => {
  const lines = content.split('\n').filter(line => line.trim());
  const services: Array<{ name: string; description: string }> = [];
  
  let greeting = '';
  let intro = '';
  let closing = '';
  
  lines.forEach((line, index) => {
    // First line is usually the greeting
    if (index === 0 && !line.includes('**')) {
      greeting = line;
      return;
    }
    
    // Look for the intro question
    if (line.includes('what kind of service') || line.includes('What kind of service')) {
      intro = line;
      return;
    }
    
    // Look for the closing line
    if (line.includes('Let me know')) {
      closing = line;
      return;
    }
    
    // Parse service items: **Service Name** (description)
    const match = line.match(/\*+([^*]+)\*+\s*\(([^)]+)\)/);
    if (match) {
      services.push({
        name: match[1].trim(),
        description: match[2].trim()
      });
    }
  });
  
  return { greeting, intro, services, closing };
};

// Helper to get icon for each service type
const getServiceIcon = (serviceName: string) => {
  if (serviceName.includes('Individual')) return FileText;
  if (serviceName.includes('Business')) return Briefcase;
  if (serviceName.includes('Planning')) return Target;
  if (serviceName.includes('Audit')) return Shield;
  return HelpCircle; // for "Other"
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [conversationSaved, setConversationSaved] = useState(false);
  const [appointmentSaved, setAppointmentSaved] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hi there! I'm Clover 🍀, The Tiny Tax Lady's lucky helper. I can help you schedule an appointment, answer questions, or just chat about what you need. What brings you by today?"
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const saveConversation = async (msgs: Message[], appointmentSaved: boolean = false) => {
    try {
      const conversationData = {
        session_id: sessionId,
        messages: msgs as any,
        appointment_requested: appointmentSaved,
        last_activity_at: new Date().toISOString(),
        status: appointmentSaved ? 'completed' : 'active',
      };

      if (!conversationSaved) {
        // First save - INSERT
        await supabase.from('chat_conversations').insert([conversationData]);
        setConversationSaved(true);
      } else {
        // Subsequent saves - UPDATE
        await supabase
          .from('chat_conversations')
          .update(conversationData)
          .eq('session_id', sessionId);
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  };

  const handleServiceClick = async (serviceName: string) => {
    if (isLoading) return;
    
    setInputValue(serviceName);
    
    const newMessages: Message[] = [...messages, { role: "user", content: serviceName }];
    setMessages(newMessages);
    setIsLoading(true);

    if (!conversationSaved) {
      await saveConversation(newMessages);
    }

    try {
      const { data, error } = await supabase.functions.invoke("chat-appointments", {
        body: { 
          messages: newMessages,
          conversationHistory: newMessages,
          sessionId,
          appointment_already_saved: appointmentSaved
        }
      });

      if (error) throw error;

      const updatedMessages: Message[] = [...newMessages, { 
        role: "assistant" as const, 
        content: data.message 
      }];
      setMessages(updatedMessages);
      await saveConversation(updatedMessages, data.appointment_saved);

      if (data.appointment_saved) {
        setAppointmentSaved(true);
        toast({
          title: "Lucky day! 🍀",
          description: "Clover saved your appointment. Diane will confirm with you soon!",
        });
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Oops!",
        description: "Clover's having a little trouble. Mind trying that again?",
        variant: "destructive",
      });
      
      const errorMessages: Message[] = [...newMessages, { 
        role: "assistant" as const, 
        content: "I apologize, but I'm having trouble processing your request. Please try again or call us directly." 
      }];
      setMessages(errorMessages);
      await saveConversation(errorMessages);
    } finally {
      setIsLoading(false);
      setInputValue("");
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    
    // Add user message
    const newMessages: Message[] = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    // Save conversation after first user message
    if (!conversationSaved) {
      await saveConversation(newMessages);
    }

    try {
      const { data, error } = await supabase.functions.invoke("chat-appointments", {
        body: { 
          messages: newMessages,
          conversationHistory: newMessages,
          sessionId,
          appointment_already_saved: appointmentSaved
        }
      });

      if (error) throw error;

      // Add AI response
      const updatedMessages: Message[] = [...newMessages, { 
        role: "assistant" as const, 
        content: data.message 
      }];
      setMessages(updatedMessages);

      // Save conversation after AI response
      await saveConversation(updatedMessages, data.appointment_saved);

      // Show success toast if appointment was saved
      if (data.appointment_saved) {
        setAppointmentSaved(true); // Prevent double-booking
        toast({
          title: "Lucky day! 🍀",
          description: "Clover saved your appointment. Diane will confirm with you soon!",
        });
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Oops!",
        description: "Clover's having a little trouble. Mind trying that again?",
        variant: "destructive",
      });
      
      // Add error message
      const errorMessages: Message[] = [...newMessages, { 
        role: "assistant" as const, 
        content: "I apologize, but I'm having trouble processing your request. Please try again or call us directly." 
      }];
      setMessages(errorMessages);
      await saveConversation(errorMessages);
    } finally {
      setIsLoading(false);
      // Refocus input after message is sent
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2 animate-fade-in">
          {/* Label Badge */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105">
            <span className="text-sm font-semibold whitespace-nowrap">ASK CLOVER</span>
          </div>
          
          {/* Chat Button */}
          <Button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-primary to-secondary hover:scale-110"
            size="icon"
            aria-label="Open chat with Clover"
          >
            <img 
              src="/images/clover-icon.png" 
              alt="Clover" 
              className="h-8 w-8 rounded-full object-cover"
            />
          </Button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-background rounded-2xl shadow-2xl flex flex-col z-50 border border-border overflow-hidden animate-scale-in md:w-96 sm:w-[calc(100vw-3rem)] sm:h-[calc(100vh-3rem)] sm:max-h-[600px]">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary via-primary to-secondary p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border-2 border-white/30 overflow-hidden bg-white shadow-md">
                <img 
                  src="/images/clover-icon.png" 
                  alt="Clover" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-semibold text-white">Clover 🍀</h3>
                <p className="text-xs text-white/80">Your Lucky Helper</p>
              </div>
            </div>
            <Button
              onClick={() => setIsOpen(false)}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-8 w-8"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-background to-muted/20">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.role === "assistant" && isServiceSelection(message.content) ? (
                  <div className="max-w-[85%] animate-fade-in">
                    {(() => {
                      const { greeting, intro, services, closing } = parseServiceSelection(message.content);
                      return (
                        <div className="space-y-3">
                          {greeting && <p className="text-sm text-foreground mb-2">{greeting}</p>}
                          {intro && <p className="text-sm text-foreground">{intro}</p>}
                          
                          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
                            <CardContent className="p-3 space-y-2">
                              {services.map((service, idx) => {
                                const Icon = getServiceIcon(service.name);
                return (
                  <div 
                    key={idx}
                    onClick={() => handleServiceClick(service.name)}
                    className={`flex items-start gap-3 p-3 rounded-lg bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 active:scale-[0.98] transition-all cursor-pointer border border-blue-100 dark:border-blue-900 hover:shadow-sm ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                                    <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                        {service.name}
                                      </div>
                                      <div className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                                        {service.description}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </CardContent>
                          </Card>
                          
                          {closing && <p className="text-sm font-medium text-foreground mt-2">{closing}</p>}
                        </div>
                      );
                    })()}
                  </div>
                ) : message.role === "assistant" && isAppointmentConfirmation(message.content) ? (
                  <div className="max-w-[85%] animate-fade-in">
                    {(() => {
                      const { intro, details, question } = parseAppointmentDetails(message.content);
                      return (
                        <div className="space-y-3">
                          {intro && <p className="text-sm text-foreground mb-2">{intro}</p>}
                          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
                            <CardContent className="p-4 space-y-3">
                              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-green-200 dark:border-green-800">
                                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                                <h4 className="font-semibold text-green-900 dark:text-green-100">Appointment Details</h4>
                              </div>
                              {details.map(({ label, value, icon: Icon }, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                  <Icon className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-green-700 dark:text-green-300">{label}</div>
                                    <div className="text-sm text-green-900 dark:text-green-50">{value}</div>
                                  </div>
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                          {question && <p className="text-sm font-medium text-foreground mt-2">{question}</p>}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 animate-fade-in ${
                      message.role === "user"
                        ? "bg-gradient-to-br from-primary to-secondary text-white"
                        : "bg-card border border-border text-foreground shadow-sm"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Clover is typing...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border bg-background">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="icon"
                className="bg-gradient-to-br from-primary to-secondary hover:opacity-90"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
